// THE ATTRIBUTION LEDGER — attributed outcomes as create-only governed artifacts.
//
// A single AttributedOutcome answers "did this action cause this improvement?".
// The ledger makes the capability COMPOUND: every governed attribution is
// persisted as a create-only artifact with provenance to BOTH the commitment that
// promised the action and the actuals that proved it. Once persisted, the renewal
// question becomes answerable over the whole year — "what has Helix-governed action
// delivered?" — as a composition over the ledger (attribution-rollup.ts).
//
// Two governance laws are enforced here:
//   1. Create-only. The ledger is append-only: appendAttributionLedgerEntry returns
//      a NEW array and refuses to overwrite an existing entryId. An attributed
//      outcome, once written, is immutable evidence — you cannot silently restate
//      last quarter's delivered dollars.
//   2. Dual provenance completeness. An entry is refused unless it is bound to a
//      commitment (decisionId + commitmentId + commitment evidence) AND to actuals
//      (source artifact + as-of + actuals evidence). An attribution with no
//      commitment or no observed actual is not a governed outcome; it is a claim.

import type { AttributedOutcome } from './attribution-engine';

export const ATTRIBUTION_LEDGER_ENTRY_SCHEMA_VERSION = 'attribution_ledger_entry_v1' as const;
export type AttributionLedgerEntrySchemaVersion =
  typeof ATTRIBUTION_LEDGER_ENTRY_SCHEMA_VERSION;

// Provenance to the commitment the action was promised under.
export type AttributionLedgerCommitmentRef = Readonly<{
  decisionId: string;
  commitmentId: string;
  curveVersion: string | null;
  finalizationPackageId: string | null;
  gateReceiptKind: string | null;
}>;

// Provenance to the actuals that proved the outcome landed.
export type AttributionLedgerActualsRef = Readonly<{
  sourceArtifactId: string;
  asOf: string;
  sourceClass: string;
  evidenceRefIds: readonly string[];
}>;

export type AttributionLedgerEntryStatus = 'attributed' | 'confounded';

export type AttributionLedgerEntry = Readonly<{
  schemaVersion: AttributionLedgerEntrySchemaVersion;
  entryId: string;
  createdAt: string;
  outcome: AttributedOutcome;
  commitmentRef: AttributionLedgerCommitmentRef;
  actualsRef: AttributionLedgerActualsRef;
  entryStatus: AttributionLedgerEntryStatus;
}>;

export type AttributionLedgerErrorCode =
  | 'invalid_entry'
  | 'not_an_attributed_outcome'
  | 'missing_commitment_provenance'
  | 'missing_actuals_provenance'
  | 'duplicate_entry_id';

export type AttributionLedgerValidationResult =
  | Readonly<{ ok: true }>
  | Readonly<{ ok: false; errorCode: AttributionLedgerErrorCode; errors: readonly string[] }>;

export type BuildAttributionLedgerEntryInput = Readonly<{
  entryId: string;
  createdAt: string;
  outcome: AttributedOutcome;
  commitmentRef: AttributionLedgerCommitmentRef;
  actualsRef: AttributionLedgerActualsRef;
}>;

export type BuildAttributionLedgerEntryResult =
  | Readonly<{ ok: true; entry: AttributionLedgerEntry }>
  | Readonly<{ ok: false; errorCode: AttributionLedgerErrorCode; errors: readonly string[] }>;

export type AppendAttributionLedgerEntryResult =
  | Readonly<{ ok: true; ledger: readonly AttributionLedgerEntry[]; entry: AttributionLedgerEntry }>
  | Readonly<{ ok: false; errorCode: AttributionLedgerErrorCode; errors: readonly string[] }>;

// Validates provenance completeness for a candidate entry. A gap outcome or an
// entry missing either half of its dual provenance is rejected with a named code.
export function validateAttributionLedgerEntry(
  input: BuildAttributionLedgerEntryInput,
): AttributionLedgerValidationResult {
  const errors: string[] = [];

  if (!isNonEmptyString(input?.entryId)) errors.push('entryId is required');
  if (!isNonEmptyString(input?.createdAt)) errors.push('createdAt is required');
  if (!input?.outcome || typeof input.outcome !== 'object') {
    errors.push('outcome is required');
    return fail('invalid_entry', errors);
  }

  const status = input.outcome.attributionStatus;
  if (status !== 'attributed' && status !== 'confounded') {
    return fail('not_an_attributed_outcome', [
      `only attributed or confounded outcomes are ledgerable (got '${status}')`,
    ]);
  }

  if (errors.length > 0) return fail('invalid_entry', errors);

  const commitmentErrors: string[] = [];
  if (!isNonEmptyString(input.commitmentRef?.decisionId)) {
    commitmentErrors.push('commitmentRef.decisionId is required');
  }
  if (!isNonEmptyString(input.commitmentRef?.commitmentId)) {
    commitmentErrors.push('commitmentRef.commitmentId is required');
  }
  if ((input.outcome.provenance?.commitmentEvidenceRefIds?.length ?? 0) === 0) {
    commitmentErrors.push('outcome.provenance.commitmentEvidenceRefIds must be non-empty');
  }
  if (commitmentErrors.length > 0) return fail('missing_commitment_provenance', commitmentErrors);

  const actualsErrors: string[] = [];
  if (!isNonEmptyString(input.actualsRef?.sourceArtifactId)) {
    actualsErrors.push('actualsRef.sourceArtifactId is required');
  }
  if (!isNonEmptyString(input.actualsRef?.asOf)) {
    actualsErrors.push('actualsRef.asOf is required');
  }
  if ((input.outcome.provenance?.actualsEvidenceRefIds?.length ?? 0) === 0) {
    actualsErrors.push('outcome.provenance.actualsEvidenceRefIds must be non-empty');
  }
  if (actualsErrors.length > 0) return fail('missing_actuals_provenance', actualsErrors);

  return { ok: true };
}

export function buildAttributionLedgerEntry(
  input: BuildAttributionLedgerEntryInput,
): BuildAttributionLedgerEntryResult {
  const validation = validateAttributionLedgerEntry(input);
  if (!validation.ok) {
    return { ok: false, errorCode: validation.errorCode, errors: validation.errors };
  }

  const entry: AttributionLedgerEntry = Object.freeze({
    schemaVersion: ATTRIBUTION_LEDGER_ENTRY_SCHEMA_VERSION,
    entryId: input.entryId,
    createdAt: input.createdAt,
    outcome: input.outcome,
    commitmentRef: Object.freeze({
      decisionId: input.commitmentRef.decisionId,
      commitmentId: input.commitmentRef.commitmentId,
      curveVersion: input.commitmentRef.curveVersion ?? null,
      finalizationPackageId: input.commitmentRef.finalizationPackageId ?? null,
      gateReceiptKind: input.commitmentRef.gateReceiptKind ?? null,
    }),
    actualsRef: Object.freeze({
      sourceArtifactId: input.actualsRef.sourceArtifactId,
      asOf: input.actualsRef.asOf,
      sourceClass: input.actualsRef.sourceClass,
      evidenceRefIds: Object.freeze([...(input.actualsRef.evidenceRefIds ?? [])]),
    }),
    entryStatus: input.outcome.attributionStatus === 'confounded' ? 'confounded' : 'attributed',
  });

  return { ok: true, entry };
}

// Create-only append. Returns a NEW ledger array; refuses a duplicate entryId and
// refuses an entry that fails provenance completeness. Never mutates the input.
export function appendAttributionLedgerEntry(
  ledger: readonly AttributionLedgerEntry[],
  input: BuildAttributionLedgerEntryInput,
): AppendAttributionLedgerEntryResult {
  const built = buildAttributionLedgerEntry(input);
  if (!built.ok) {
    return { ok: false, errorCode: built.errorCode, errors: built.errors };
  }

  const existing = ledger ?? [];
  if (existing.some((entry) => entry.entryId === built.entry.entryId)) {
    return {
      ok: false,
      errorCode: 'duplicate_entry_id',
      errors: [
        `entryId '${built.entry.entryId}' already exists; the attribution ledger is create-only and cannot be overwritten`,
      ],
    };
  }

  return {
    ok: true,
    ledger: Object.freeze([...existing, built.entry]),
    entry: built.entry,
  };
}

function fail(
  errorCode: AttributionLedgerErrorCode,
  errors: readonly string[],
): AttributionLedgerValidationResult {
  return { ok: false, errorCode, errors: Object.freeze([...errors]) };
}

function isNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}
